import type { VNode } from "vue"
export type { VNode as Element } from "vue"
type Element = VNode
import {
  computed,
  createApp,
  defineComponent,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  nextTick,
  watchEffect,
} from "vue"
import type {
  NextFrame as NextFrameType,
} from "../base/Factory"
import { CreateReactivityFunctions } from "../base/Factory"
import { RunAllWith } from "../utils/Lifecycle"
import { DefineProperty } from "../utils/Properties"

const constructors = {
  set_data: (instance: any, key: string, value: any) => {
    const r = ref(value)
    DefineProperty(
      instance,
      key,
      () => r.value,
      v => (r.value = v)
    )
  },
  set_computed: (
    instance: any,
    key: string,
    get: () => any,
    set: (v: any) => void
  ) => {
    const r = computed(get)
    DefineProperty(instance, key, () => r.value, set)
  },
  init: reactive,
  effect: watchEffect,
}

const mountVue = (render: () => Element | undefined, el: HTMLElement) =>
  createApp({ render }).mount(el)

// Component factory that creates a Vue component with persistent state
const vueComponentFactory = (
  init: (props: any) => any,
  config: {
    render?: (children?: Element[]) => Element | undefined
    mounted?: any
    unmounted?: any
  }
) =>
  defineComponent({
    inheritAttrs: false,
    setup(_, { attrs, slots }) {
      const instance = init(attrs)
      const vueInstance = getCurrentInstance()
      onMounted(() => RunAllWith(config.mounted, instance, vueInstance?.proxy?.$el))
      onUnmounted(() => RunAllWith(config.unmounted, instance, vueInstance?.proxy?.$el))
      return () => config.render?.call(instance, slots.default?.())
    },
  })

export { CreateMixin } from "../base/Factory"
export const NextFrame: NextFrameType = nextTick
export const { Init, CreateRenderFunction, Mount } = CreateReactivityFunctions<Element>(
  constructors,
  mountVue,
  vueComponentFactory as any
)
